import { useMemo } from 'react';
import QRCodeLib from 'qrcode';

/**
 * Ticket QR code.
 *
 * The encoder is the `qrcode` library rather than hand-rolled: a code that does
 * not scan strands a passenger at the gate, and QR has enough detail (masking,
 * block interleaving, format bits) that "looks like a QR code" is not evidence it
 * works. The library is bundled, so the ticket still renders with no network
 * (docs/05 "PWA behavior": QR must work offline).
 *
 * Rendering is our own SVG so the code stays crisp at any size and needs no
 * canvas, image request, or `dangerouslySetInnerHTML`.
 */

export interface QrCodeProps {
  value: string;
  size?: number;
  label?: string;
}

interface QrMatrix {
  size: number;
  isDark: (row: number, col: number) => boolean;
}

function buildMatrix(value: string): QrMatrix | null {
  try {
    // Level M matches the printed-ticket convention: ~15% of the code can be
    // damaged or obscured and it still reads.
    const qr = QRCodeLib.create(value, { errorCorrectionLevel: 'M' });
    const { size, data } = qr.modules;
    return { size, isDark: (row, col) => data[row * size + col] === 1 };
  } catch {
    return null;
  }
}

export function QrCode({ value, size = 200, label }: QrCodeProps) {
  const matrix = useMemo(() => buildMatrix(value), [value]);

  if (!matrix) {
    // Show the readable code rather than a broken image, so a passenger can
    // still be checked in by reference.
    return (
      <p className="tabular text-center text-sm font-semibold" aria-label={label}>
        {value}
      </p>
    );
  }

  const quiet = 4;
  const total = matrix.size + quiet * 2;

  let path = '';
  for (let row = 0; row < matrix.size; row++) {
    for (let col = 0; col < matrix.size; col++) {
      if (matrix.isDark(row, col)) path += `M${col + quiet} ${row + quiet}h1v1h-1z`;
    }
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${total} ${total}`}
      role="img"
      aria-label={label ?? 'Ticket QR code'}
      shapeRendering="crispEdges"
    >
      {/* Always black on white: scanners need real contrast, so this one element
          deliberately ignores the tenant palette. */}
      <rect width={total} height={total} fill="#FFFFFF" />
      <path d={path} fill="#000000" />
    </svg>
  );
}
